"use client";
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "next-intl";
import { useInView } from "react-intersection-observer";
import type { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import ProductCard from "@/components/ProductCard";
import { ChevronLeft, ChevronRight, Circle, Clock } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
  FormatSubType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { cn } from "@/lib/utils";
import { makeSafeAttributeString } from "@/utils/functions/getIdLabel";
import { TagularVariant } from "@/utils/tagular/dataTranslationMatrices";
import type { RecentlyViewedProductCardListClientProps } from "./types";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import { convertToRecentlyViewedCard } from "@/utils/convertToRecentlyViewedCard";
import { useDotButton } from "@/components/Carousel/CarouselDotButton";
import { usePrevNextButtons } from "@/components/Carousel/CarouselArrowButtons";
import { formatViewedLabel } from "./utils/formatViewedLabel";

const emblaOptions: EmblaOptionsType = {
  loop: false,
  align: "center",
  containScroll: "trimSnaps",
  slidesToScroll: 1,
};

export default function RecentlyViewedProductCardListClient({
  id = "",
  title = "Recently Viewed",
  cardFormatSubtype = FormatSubType.Grid,
  cardWrapperClassName = "",
  contractText = "",
  className = "",
  container_class,
  expandText = "",
  initialCardsLength = 4,
  isProfEdPage = false,
  location = "",
  products,
  showAllProducts = false,
  maxRows = 2,
}: RecentlyViewedProductCardListClientProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const locale = useLocale();
  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const expandLabel =
    expandText || (locale === "es" ? "Ver más" : "Show more recent activity");
  const contractLabel =
    contractText ||
    (locale === "es" ? "Ver menos" : "Show less recent activity");

  const tagular_variant = isProfEdPage
    ? TagularVariant.ProfessionalCertificate
    : undefined;

  const webElement: ElementClicked["webElement"] = {
    location,
    position: "1",
    elementType: ElementType.Link,
    htmlId: "show-more-link",
    text: isExpanded
      ? "Show less recent activity"
      : "Show more recent activity",
    name: isExpanded
      ? hyphenateForTagular("Show Less")
      : hyphenateForTagular("Show More"),
  };

  const { elementClicked, elementViewed } = useElementEvents({ webElement });
  const { ref: showMoreRef, inView: showMoreInView } = useInView(
    DEFAULT_USE_IN_VIEW_OPTIONS,
  );

  useEffect(() => {
    if (showMoreInView) elementViewed();
  }, [showMoreInView, elementViewed]);

  const initialProducts = useMemo(
    () => products.slice(0, initialCardsLength),
    [products, initialCardsLength],
  );

  const COLS_PER_ROW = 4;
  const cappedProducts = useMemo(
    () =>
      showAllProducts ? products : products.slice(0, maxRows * COLS_PER_ROW),
    [products, showAllProducts, maxRows],
  );

  const titleId = makeSafeAttributeString(title);
  const skipEvents: typeof products = isExpanded ? initialProducts : [];
  const shouldShowCarouselControls = scrollSnaps.length > 1;
  const shouldShowMore =
    !showAllProducts && cappedProducts.length > initialCardsLength;

  const handleShowButtonClick = () => {
    elementClicked();
    setIsExpanded(!isExpanded);
  };

  const rtf = useMemo(
    () => new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
    [locale],
  );
  // Compute cards once for all cappedProducts, then slice for desktop expand/collapse.
  // Mobile always shows all cappedProducts; desktop shows initialCardsLength until expanded.
  const allCards = useMemo(
    () =>
      cappedProducts.map((product, index) => {
        const cardProps = convertToRecentlyViewedCard(product);
        const minutesAgo = cardProps.viewedSince ?? 0;
        const viewedLabel = formatViewedLabel(minutesAgo, rtf);
        return { product, cardProps, viewedLabel, index };
      }),
    [cappedProducts, rtf],
  );

  // Mobile shows all; desktop respects expand/collapse
  const cards =
    isExpanded || showAllProducts
      ? allCards
      : allCards.slice(0, initialCardsLength);

  function cardItem(
    { product, cardProps, viewedLabel, index }: (typeof cards)[number],
    wrapperClassName?: string,
  ) {
    return (
      <div
        key={`${location}-${product.productUuid}`}
        className={wrapperClassName}
      >
        <ProductCard
          {...cardProps}
          location={location}
          index={index}
          formatSubtype={cardFormatSubtype}
          skipViewEvent={skipEvents?.includes(product)}
          tagular_variant={tagular_variant}
          className="no-underline hover:no-underline md:!max-w-full"
        />
        <p className="text-md text-gray-80 mb-0 text-center md:text-left">
          Viewed {viewedLabel}
        </p>
      </div>
    );
  }

  if (products.length === 0) return null;

  return (
    <>
      <a className="subnav-item" id={titleId}></a>
      <div
        className={cn(
          "flex flex-col max-w-[1280px] mx-auto w-full gap-8",
          cardWrapperClassName,
        )}
      >
        {title && (
          <h2 className="my-0 text-primary text-center md:text-left md:text-3xl font-bold">
            <span className="inline-flex gap-2 items-center justify-center break-words whitespace-normal">
              <Clock svgProps={{ width: 34, height: 34 }} />
              {title}
            </span>
          </h2>
        )}

        {/* Mobile: Embla carousel */}
        <div className="md:hidden flex flex-col gap-8">
          <div
            className="overflow-hidden"
            ref={emblaRef}
            role="region"
            aria-label="Recently viewed products carousel"
            aria-roledescription="carousel"
          >
            <div className="flex">
              {allCards.map((card) =>
                cardItem(card, "flex flex-col flex-[0_0_100%] min-w-0"),
              )}
            </div>
          </div>

          {shouldShowCarouselControls && (
            <div className="w-full flex flex-row justify-center">
              <div className="flex flex-row justify-center w-fit items-center gap-2 bg-white rounded-full border border-putty-400">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onPrevButtonClick}
                  disabled={prevBtnDisabled}
                  aria-label="Previous slide"
                  className="hover:bg-transparent"
                >
                  <ChevronLeft />
                </Button>

                <div className="flex gap-6">
                  {scrollSnaps.map((_, i) => (
                    <Button
                      key={i}
                      variant="ghost"
                      size="icon"
                      onClick={() => onDotButtonClick(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      className={`h-3 w-3 rounded-full p-0 ${
                        i === selectedIndex ? "bg-brand-500" : "bg-primary-500"
                      }`}
                    >
                      <Circle
                        svgProps={{
                          width: "8",
                          height: "8",
                          className:
                            i === selectedIndex
                              ? "text-brand-500"
                              : "text-primary-500",
                        }}
                      />
                    </Button>
                  ))}
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onNextButtonClick}
                  disabled={nextBtnDisabled}
                  aria-label="Next slide"
                  className="hover:bg-transparent"
                >
                  <ChevronRight />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: dynamic grid */}
        <div
          className={cn(
            "hidden md:grid",
            container_class || `dynamic-grid flex-wrap gap-4 ${className}`,
          )}
        >
          {cards.map((card) => cardItem(card, "flex flex-col gap-1"))}
        </div>

        {shouldShowMore && (
          <div className="hidden md:flex justify-center">
            <span
              role="button"
              ref={showMoreRef}
              className="cursor-pointer underline text-base hover:no-underline hover:font-bold"
              onClick={handleShowButtonClick}
            >
              {isExpanded ? contractLabel : expandLabel}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
